import React from "react";

import ChatBubbleIcon from "@uui/MessageChatSquare";
import MessageCheckSquare from "@uui/MessageCheckSquare";
import { useParams } from "react-router-dom";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import { TranscriptUnauthorized } from "@app/analytics/MeetingReport/Transcript/EmptyTranscript";
import { platformDoesNotSupportRecording } from "@app/analytics/MeetingReport/utils";
import useReportShare from "@app/common/access/ShareReport/hooks/useReportShare";
import { hasRecapOnlyAccess } from "@app/common/access/ShareReport/utils";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";

import Button from "@rui/Button";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateSubtextLink,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Link from "@rui/Link";

const UnsupportedLanguage = ({ sessionId }: { sessionId: string }) => (
  <>
    <EmptyStateSubtext>
      No transcript was generated because the primary language detected is not
      supported.
    </EmptyStateSubtext>
    <Link to={ROUTES.meetingReportTabById(sessionId, "deep-dive")}>
      <Button variant="gradient">View Deep Dive</Button>
    </Link>
  </>
);

const UpsellPaid = () => (
  <>
    <EmptyStateSubtext>
      <EmptyStateSubtextLink href={ROUTES.UPGRADE_ACCOUNT}>
        Upgrade your plan
      </EmptyStateSubtextLink>{" "}
      to access meeting transcripts, summaries, topics, and more in your next
      meeting.
    </EmptyStateSubtext>
    <Link href={ROUTES.UPGRADE_ACCOUNT}>
      <Button variant="gradient">Upgrade plan</Button>
    </Link>
  </>
);

const UpsellRecording = () => (
  <>
    <EmptyStateSubtext>
      Transcription was not enabled or there was no speech detected.{" "}
      <EmptyStateSubtextLink href={ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK}>
        Enable recording
      </EmptyStateSubtextLink>{" "}
      to access meeting transcripts, summaries, topics, and more in your next
      meeting.
    </EmptyStateSubtext>
    <Link href={ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK}>
      <Button variant="gradient">Enable recording</Button>
    </Link>
  </>
);

const Default = ({
  transcriptionEnabled,
  upsellTranscriptionOptIn,
}: {
  transcriptionEnabled: boolean;
  upsellTranscriptionOptIn: boolean;
}) => {
  const { sessionId } = useParams();
  const { accessList } = useReportShare({ sessionId: sessionId! });

  return (
    <>
      <EmptyStateSubtext>
        {transcriptionEnabled
          ? "There was no speech detected."
          : "Transcription was not enabled."}
        {!transcriptionEnabled && upsellTranscriptionOptIn && (
          <>
            {" "}
            <EmptyStateSubtextLink
              href={ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK}
            >
              Enable transcription
            </EmptyStateSubtextLink>{" "}
            to access meeting transcripts, summaries, topics, and more in your
            next meeting.
          </>
        )}
      </EmptyStateSubtext>
      {!hasRecapOnlyAccess(accessList.currentUserAccessLevel) && (
        <Link to={ROUTES.meetingReportTabById(sessionId!, "deep-dive")}>
          <Button variant="gradient">View Deep Dive</Button>
        </Link>
      )}
    </>
  );
};

const EmptyRecapContent = () => {
  const {
    data: {
      isUnsupportedLanguage,
      meetingPlatform,
      sessionId,
      transcriptionEnabled: transcriptionEnabledForSession,
    },
  } = useSessionMetadata();
  const { canEditTranscriptionOptIn } = useFeatureAccess();
  const { currentPlan } = useBillingContext();
  const { userSettings } = useUserSettings();

  const upsellTranscriptionOptIn =
    !userSettings.transcriptionOptIn && canEditTranscriptionOptIn;

  if (isUnsupportedLanguage) {
    return <UnsupportedLanguage sessionId={sessionId} />;
  }

  if (!transcriptionEnabledForSession || upsellTranscriptionOptIn) {
    return (
      <Default
        transcriptionEnabled={transcriptionEnabledForSession}
        upsellTranscriptionOptIn={upsellTranscriptionOptIn}
      />
    );
  }

  // TODO (ENG-6481) - use the user's plan at the time of report
  //   generation instead of the user's current plan
  if (isFreePlan(currentPlan.type)) {
    return <UpsellPaid />;
  }

  // don't upsell enabling automatic recording if that wouldn't have mattered for
  // this meeting or if recording is already enabled
  if (
    platformDoesNotSupportRecording(meetingPlatform) ||
    userSettings.automaticRecordingEnabled
  ) {
    return (
      <Default
        transcriptionEnabled={transcriptionEnabledForSession}
        upsellTranscriptionOptIn={upsellTranscriptionOptIn}
      />
    );
  }

  return <UpsellRecording />;
};

const EmptyRecap = () => {
  const { canAccessTranscript } = useFeatureAccess();

  if (canAccessTranscript) {
    return (
      <EmptyStateContainer>
        <EmptyStateIcon Icon={MessageCheckSquare} />
        <EmptyStateTitle>No meeting notes or transcript</EmptyStateTitle>
        <EmptyRecapContent />
      </EmptyStateContainer>
    );
  }

  return (
    <EmptyStateContainer>
      <EmptyStateIcon Icon={ChatBubbleIcon} />
      <TranscriptUnauthorized />
    </EmptyStateContainer>
  );
};

export default EmptyRecap;
